import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../HeaderBar";
import Sidebar from "../../Sidebar";
import LoadingTablePage from "../../../pages/LoadingTablePage";
import ConfirmationModal from "../../ConfirmationModal";
import { useAuth, Roles } from "../../../providers/AuthContext";
import { getAllCountries } from "../../../api/Resources";
import {
    createTariff,
    deleteTariff,
    previewTariffAssignmentConflicts,
    updateTariff,
    updateTariffElement,
} from "../../../api/Tariffs";
import {
    getAllChargingConnectorsSimplified,
    getChargingConnectorsSimplifiedByOperatorOrganizationPublicId,
} from "../../../api/ChargingConnector";
import { getAllInternalCustomers } from "../../../api/Customers";
import {
    getCustomerDisplayName,
    getOrganizationDisplayName,
    shouldShowOrganizationName,
} from "../../../helpers/CustomerDisplay";
import BasicsStep from "./BasicsStep";
import EnergyPriceStep from "./EnergyPriceStep";
import ExtrasStep from "./ExtrasStep";
import AssignmentStep from "./AssignmentStep";
import PreviewStep from "./PreviewStep";
import {
    ENERGY_MODE,
    WIZARD_STEPS,
    buildDecimalOrNull,
    buildPriceObject,
    createEmptyWizardState,
    fromCompleteTariff,
    getCreateSeedElements,
    toApiDateTime,
    toElements,
    validateBasics,
    validateEnergy,
    validatePreauthorizeAmount,
    formatElementsInUseError,
} from "./tariffWizardState";

const mergeConnectorOptions = (...lists) => {
    const byId = new Map();
    lists.flat().forEach((connector) => {
        if (connector?.chargingConnectorPublicId) {
            byId.set(connector.chargingConnectorPublicId, connector);
        }
    });
    return Array.from(byId.values());
};

const mergeCustomerOptions = (fetched, assigned, includeOrganization) => {
    const byId = new Map();
    [...(fetched || []), ...(assigned || [])].forEach((customer) => {
        if (!customer?.customerPublicId || byId.has(customer.customerPublicId)) {
            return;
        }
        byId.set(customer.customerPublicId, {
            ...customer,
            customerName: getCustomerDisplayName(customer, includeOrganization),
        });
    });
    return Array.from(byId.values());
};

const TariffWizard = ({
    mode = "create",
    tariffPublicId = null,
    initialTariff = null,
    initialStep = 0,
    onCancel,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getAccessToken, hasRole } = useAuth();
    const [step, setStep] = useState(initialStep);
    const [state, setState] = useState(() =>
        initialTariff ? fromCompleteTariff(initialTariff) : createEmptyWizardState()
    );
    const [countries, setCountries] = useState([]);
    const [connectors, setConnectors] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [invalidFields, setInvalidFields] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pendingIsPublic, setPendingIsPublic] = useState(null);
    const [publicConfirmMessage, setPublicConfirmMessage] = useState("");
    const [conflicts, setConflicts] = useState([]);
    const [conflictOpen, setConflictOpen] = useState(false);

    const operatorOrganizationPublicId =
        initialTariff?.organization?.organizationPublicId || null;

    useEffect(() => {
        loadLookups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operatorOrganizationPublicId]);

    const loadLookups = async () => {
        setLoading(true);
        try {
            const token = await getAccessToken();
            const includeOrganization = shouldShowOrganizationName(hasRole);
            const connectorPromise = operatorOrganizationPublicId
                ? getChargingConnectorsSimplifiedByOperatorOrganizationPublicId(
                    operatorOrganizationPublicId,
                    token
                )
                : getAllChargingConnectorsSimplified(token);
            const [countryList, fetchedConnectors, fetchedCustomers] = await Promise.all([
                getAllCountries(token),
                connectorPromise,
                getAllInternalCustomers(token),
            ]);
            setCountries(
                (countryList || []).map((country) => ({
                    label: country.countryName,
                    value: country.alpha2CountryCode,
                }))
            );
            setConnectors(
                mergeConnectorOptions(initialTariff?.chargingConnectors || [], fetchedConnectors || [])
            );
            setCustomers(
                mergeCustomerOptions(
                    fetchedCustomers,
                    initialTariff?.customers,
                    includeOrganization
                )
            );
        } catch (err) {
            console.error(err);
            toast.error(err?.message || t("tariff.failedLoadWizard"));
        }
        setLoading(false);
    };

    const patchBasics = (key, value) => {
        if (key === "isPublic") {
            handleIsPublicChange(value);
            return;
        }
        setState((prev) => ({
            ...prev,
            basics: { ...prev.basics, [key]: value },
        }));
    };

    const handleIsPublicChange = (value) => {
        if (value === true && state.assignment.customerPublicIds.length > 0) {
            setPendingIsPublic(true);
            setPublicConfirmMessage(t("confirm.makePublicClearCustomers"));
            return;
        }
        if (value === true) {
            setPendingIsPublic(true);
            setPublicConfirmMessage(t("confirm.makePublic"));
            return;
        }
        if (value === false) {
            setPendingIsPublic(false);
            setPublicConfirmMessage(t("confirm.removePublic"));
            return;
        }
        finishIsPublicChange(value);
    };

    const finishIsPublicChange = (value) => {
        setState((prev) => ({
            ...prev,
            basics: { ...prev.basics, isPublic: value },
            energyMode:
                value && prev.energyMode === ENERGY_MODE.SPOT
                    ? ENERGY_MODE.FIXED
                    : prev.energyMode,
            energy:
                value && prev.energyMode === ENERGY_MODE.SPOT
                    ? {
                          ...prev.energy,
                          energyPriceMinExclVat: "",
                          energyPriceMaxExclVat: "",
                      }
                    : prev.energy,
            assignment: {
                ...prev.assignment,
                customerPublicIds: value ? [] : prev.assignment.customerPublicIds,
            },
        }));
    };

    const canClickStep = (index) => {
        if (index === step) {
            return false;
        }
        if (mode === "edit") {
            return true;
        }
        return index < step;
    };

    const blockOnBasics = () => {
        const missing = validateBasics(state);
        const preauthError = validatePreauthorizeAmount(state);
        if (!missing.length && !preauthError) {
            return false;
        }
        setInvalidFields(preauthError ? [...missing, "preauthorizeAmount"] : missing);
        if (missing.length) {
            toast.error(
                t("common.pleaseFill", {
                    fields: missing
                        .map((field) =>
                            t(
                                field === "tariffName"
                                    ? "common.name"
                                    : field === "currency"
                                        ? "common.currency"
                                        : "common.country"
                            )
                        )
                        .join(", "),
                })
            );
        }
        if (preauthError) {
            toast.error(t(preauthError));
        }
        return true;
    };

    const goNext = () => {
        if (step === 0) {
            if (blockOnBasics()) {
                return;
            }
            setInvalidFields([]);
        }
        if (step === 1) {
            const energyError = validateEnergy(state);
            if (energyError) {
                toast.error(t(energyError));
                return;
            }
        }
        setStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
    };

    const saveTariff = async ({ replaceOverlapping = false } = {}) => {
        if (blockOnBasics()) {
            setStep(0);
            return;
        }
        const energyError = validateEnergy(state);
        if (energyError) {
            toast.error(t(energyError));
            setStep(1);
            return;
        }

        setSaving(true);
        const toastId = toast.loading(
            mode === "create"
                ? t("toast.creating", { entity: t("entity.tariff") })
                : t("toast.updating", { entity: t("entity.tariff") })
        );
        const elements = toElements(state).filter((element) => !element.isDeleted);
        let createdId = null;
        try {
            const token = await getAccessToken();
            let currentId = tariffPublicId;

            if (mode === "create" && !currentId) {
                const created = await createTariff(
                    {
                        countryCode: state.basics.countryCode,
                        currency: state.basics.currency,
                        tariffType: state.basics.tariffType,
                        tariffName: state.basics.tariffName,
                        taxIncluded: "NO",
                        tariffElements: getCreateSeedElements(state),
                        isPublic: state.basics.isPublic || false,
                    },
                    token
                );
                currentId = created.tariffPublicId;
                createdId = currentId;
            }

            await updateTariffElement(
                { TariffPublicId: currentId, TariffElements: elements },
                token
            );

            await updateTariff(
                {
                    tariffPublicId: currentId,
                    tariffName: state.basics.tariffName,
                    currency: state.basics.currency,
                    tariffType: state.basics.tariffType,
                    taxIncluded: "NO",
                    isPublic: state.basics.isPublic || false,
                    startDateTimeUtc: toApiDateTime(state.basics.startDateTimeUtc),
                    endDateTimeUtc: toApiDateTime(state.basics.endDateTimeUtc),
                    minPrice: buildPriceObject(
                        state.basics.minPriceExclVat,
                        state.basics.minPriceInclVat
                    ),
                    maxPrice: buildPriceObject(
                        state.basics.maxPriceExclVat,
                        state.basics.maxPriceInclVat
                    ),
                    preauthorizeAmount: buildDecimalOrNull(state.basics.preauthorizeAmount),
                    isSpotEnergy:
                        !state.basics.isPublic && state.energyMode === ENERGY_MODE.SPOT,
                    energyPriceMinExclVat:
                        !state.basics.isPublic && state.energyMode === ENERGY_MODE.SPOT
                            ? buildDecimalOrNull(state.energy.energyPriceMinExclVat)
                            : null,
                    energyPriceMaxExclVat:
                        !state.basics.isPublic && state.energyMode === ENERGY_MODE.SPOT
                            ? buildDecimalOrNull(state.energy.energyPriceMaxExclVat)
                            : null,
                    tariffAltTexts: (state.basics.altTexts || []).filter(
                        (alt) => alt.language && alt.text
                    ),
                    customerPublicIds: state.basics.isPublic ? [] : state.assignment.customerPublicIds,
                    chargingConnectorPublicIds: state.assignment.chargingConnectorPublicIds,
                    replaceOverlapping,
                },
                token
            );

            toast.success(
                mode === "create"
                    ? t("toast.created", { entity: t("entity.tariff") })
                    : t("toast.updated", { entity: t("entity.tariff") }),
                { id: toastId }
            );
            navigate(`/tariffs/${currentId}`);
        } catch (err) {
            if (createdId) {
                const token = await getAccessToken();
                try {
                    await deleteTariff(createdId, token);
                } catch {
                    // Keep the original save error; the stub tariff may remain.
                }
            }
            toast.error(
                formatElementsInUseError(
                    err?.message,
                    initialTariff?.tariffElements,
                    elements,
                    t
                ) || t("tariff.failedSave"),
                { id: toastId }
            );
        }
        setSaving(false);
    };

    const handleSaveClick = async () => {
        if (!state.assignment.chargingConnectorPublicIds.length) {
            await saveTariff({ replaceOverlapping: false });
            return;
        }

        try {
            const token = await getAccessToken();
            const result = await previewTariffAssignmentConflicts(
                {
                    tariffPublicId,
                    tariffType: state.basics.tariffType,
                    isPublic: state.basics.isPublic || false,
                    startDateTimeUtc: toApiDateTime(state.basics.startDateTimeUtc),
                    endDateTimeUtc: toApiDateTime(state.basics.endDateTimeUtc),
                    chargingConnectorPublicIds: state.assignment.chargingConnectorPublicIds,
                    customerPublicIds: state.basics.isPublic ? [] : state.assignment.customerPublicIds,
                },
                token
            );
            const nextConflicts = result?.conflicts || result || [];
            if (Array.isArray(nextConflicts) && nextConflicts.length > 0) {
                setConflicts(nextConflicts);
                setConflictOpen(true);
                return;
            }
        } catch (err) {
            toast.error(err?.message || t("tariff.couldNotCheckConflicts"));
            return;
        }

        await saveTariff({ replaceOverlapping: false });
    };

    const conflictTitle = useMemo(
        () => formatConflictMessage(conflicts, connectors, t),
        [conflicts, connectors, t]
    );

    const renderStep = () => {
        if (step === 0) {
            return (
                <BasicsStep
                    basics={state.basics}
                    onChange={patchBasics}
                    invalidFields={invalidFields}
                    countryOptions={countries}
                    countryDisabled={mode === "edit"}
                />
            );
        }
        if (step === 1) {
            return (
                <EnergyPriceStep
                    energyMode={state.energyMode}
                    energy={state.energy}
                    currency={state.basics.currency}
                    isPublic={state.basics.isPublic}
                    onModeChange={(energyMode) => setState((prev) => ({ ...prev, energyMode }))}
                    onEnergyChange={(energy) => setState((prev) => ({ ...prev, energy }))}
                />
            );
        }
        if (step === 2) {
            return (
                <ExtrasStep
                    extras={state.extras}
                    currency={state.basics.currency}
                    onChange={(extras) => setState((prev) => ({ ...prev, extras }))}
                />
            );
        }
        if (step === 3) {
            return (
                <AssignmentStep
                    isPublic={state.basics.isPublic}
                    connectors={connectors}
                    selectedConnectorIds={state.assignment.chargingConnectorPublicIds}
                    onConnectorsChange={(chargingConnectorPublicIds) =>
                        setState((prev) => ({
                            ...prev,
                            assignment: { ...prev.assignment, chargingConnectorPublicIds },
                        }))
                    }
                    customerPublicIds={state.assignment.customerPublicIds}
                    onCustomersChange={(customerPublicIds) =>
                        setState((prev) => ({
                            ...prev,
                            assignment: { ...prev.assignment, customerPublicIds },
                        }))
                    }
                    customers={customers}
                />
            );
        }
        return (
            <PreviewStep
                state={state}
                connectors={connectors}
                customers={customers}
                countryOptions={countries}
                organizationName={
                    hasRole(Roles.Admin) ? getOrganizationDisplayName(initialTariff) : ""
                }
            />
        );
    };

    const title =
        mode === "create" ? t("pages.createTariff") : state.basics.tariffName || t("pages.editTariff");

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={title} loading={loading} />
                    </div>
                    {!loading && (
                        <div className="page-content">
                            <div className="flex flex-row items-center pb-4">
                                <button
                                    type="button"
                                    onClick={() => (onCancel ? onCancel() : navigate(-1))}
                                    className="text-black flex"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                                </button>
                                <h2 className="border-l border-mute-200 pl-4">{title}</h2>
                            </div>

                            <div className="grid grid-cols-5 gap-1.5 sm:gap-3 mb-8">
                                {WIZARD_STEPS.map((stepKey, index) => {
                                    const isCurrent = step === index;
                                    const clickable = canClickStep(index);
                                    return (
                                        <button
                                            key={stepKey}
                                            type="button"
                                            disabled={!clickable}
                                            onClick={() => clickable && setStep(index)}
                                            className="flex min-w-0 flex-col items-center text-center"
                                        >
                                            <div
                                                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition ${
                                                    isCurrent
                                                        ? "bg-blue-500 text-white"
                                                        : "bg-mute-200 text-mute-600"
                                                }`}
                                            >
                                                {index + 1}
                                            </div>
                                            <div className="mt-1 text-xs font-semibold text-mute-700 sm:mt-2 sm:text-sm">
                                                {t(`tariff.steps.${stepKey}`)}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {renderStep()}

                            <div className="mt-8 flex items-center justify-between border-t border-mute-200 pt-4">
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => (onCancel ? onCancel() : navigate(-1))}
                                        disabled={saving}
                                    >
                                        {t("common.cancel")}
                                    </button>
                                    {step > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={() => setStep((prev) => prev - 1)}
                                            disabled={saving}
                                        >
                                            {t("common.back")}
                                        </button>
                                    )}
                                </div>
                                {step < WIZARD_STEPS.length - 1 ? (
                                    <button type="button" className="btn btn-primary" onClick={goNext}>
                                        {t("common.next")}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSaveClick}
                                        disabled={saving}
                                    >
                                        {t("common.save")}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </main>
            </div>

            <ConfirmationModal
                isOpen={pendingIsPublic !== null}
                title={publicConfirmMessage}
                onClose={() => setPendingIsPublic(null)}
                onConfirm={() => {
                    finishIsPublicChange(pendingIsPublic);
                    setPendingIsPublic(null);
                }}
            />
            <ConfirmationModal
                isOpen={conflictOpen}
                title={conflictTitle}
                okText={t("confirm.yesReplace")}
                onClose={() => setConflictOpen(false)}
                onConfirm={async () => {
                    setConflictOpen(false);
                    await saveTariff({ replaceOverlapping: true });
                }}
            />
        </>
    );
};

function formatConflictMessage(conflicts, connectors, t) {
    if (!conflicts.length) {
        return t("confirm.replaceExistingTariffs");
    }

    const byKey = new Map();
    conflicts.forEach((conflict) => {
        const connector = connectors.find(
            (item) => item.chargingConnectorPublicId === conflict.chargingConnectorPublicId
        );
        const connectorName =
            conflict.chargingConnectorName || connector?.chargingConnectorName || conflict.chargingConnectorPublicId;
        const siteName = connector?.chargingSiteName;
        const key = `${conflict.kind}|${conflict.existingTariffPublicId}|${conflict.customerPublicId || ""}`;
        if (!byKey.has(key)) {
            byKey.set(key, {
                ...conflict,
                connectors: [],
            });
        }
        byKey.get(key).connectors.push(siteName ? `${connectorName} (${siteName})` : connectorName);
    });

    const lines = [...byKey.values()].map((group) => {
        if (group.kind === "customer") {
            return t("tariff.conflictCustomer", {
                customer: group.customerName || group.customerPublicId,
                tariff: group.existingTariffName,
                connectors: group.connectors.join(", "),
            });
        }
        if (group.connectors.length === 1) {
            return t("tariff.conflictConnector", {
                connector: group.connectors[0],
                tariff: group.existingTariffName,
            });
        }
        return t("tariff.conflictConnectors", {
            connectors: group.connectors.join(", "),
            tariff: group.existingTariffName,
        });
    });

    return `${lines.join("\n\n")}\n\n${t("tariff.replaceIt")}`;
}

export default TariffWizard;
